import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { LoanApplicationTask } from '../../shared/models';
import { getResponseData } from '../../shared/utils/axios';
import { CerebroTextButton } from '../../shared/components';
import { CerebroApiEndpoints } from '../../shared/utils/api';

export const DeleteDocumentDialog: React.FC<DeleteDocumentDialogProps> = ({
	open = false,
	onClose,
	document: requiredDocument,
}) => {
	const api = useApi();
	const { guid } = useParams<'guid'>();

	const snackbar = useSnackbar();
	const queryClient = useQueryClient();

	const mutation = useMutation<LoanApplicationTask, unknown, LoanApplicationTask>(
		documentToDelete => {
			return getResponseData(
				api.delete<LoanApplicationTask>(
					CerebroApiEndpoints.RequiredDocuments.Specific(guid, documentToDelete.guid)
				)
			);
		},
		{
			onSuccess: (_data, deletedDocument) => {
				queryClient.invalidateQueries(QueryKeys.RequiredDocuments.Root(guid));
				snackbar.enqueueSnackbar(`Request for '${deletedDocument.applicationTaskTitle}' deleted.`, {
					variant: 'success',
				});
			},
			onError: () => {
				queryClient.invalidateQueries(QueryKeys.RequiredDocuments.Root(guid));
				snackbar.enqueueSnackbar('There was a problem deleting the required document.', { variant: 'error' });
			},
			onSettled: onClose,
		}
	);

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogTitle>Confirm Delete</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to delete the &lsquo;{requiredDocument.applicationTaskTitle}&rsquo; required
					document?
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={onClose}>No</CerebroTextButton>
				<CerebroTextButton onClick={() => mutation.mutate(requiredDocument)}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};

interface DeleteDocumentDialogProps {
	open?: boolean;
	onClose: () => void;
	document: LoanApplicationTask;
}
